import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Phone, Mail, MapPin, Facebook, Linkedin, Instagram } from 'lucide-react';
import siteConfig from '../../config/siteConfig';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-2 text-white">
              <ShieldCheck className="w-7 h-7 text-emerald-400" />
              <span className="font-heading text-xl font-semibold">FGS</span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed">
              Spécialiste de la sécurité à Montpellier. Installation et maintenance de systèmes d'alarme Ajax Systems.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-sm font-medium text-white mb-6 uppercase tracking-wider">
              Produits
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/shop" className="text-white/60 hover:text-white transition-colors text-sm">
                  Boutique - Gamme Basic
                </Link>
              </li>
              <li>
                <Link to="/superior-alarm-g2" className="text-white/60 hover:text-white transition-colors text-sm">
                  Gamme Pro / Superior
                </Link>
              </li>
              <li>
                <Link to="/superior-ssi" className="text-white/60 hover:text-white transition-colors text-sm">
                  Sécurité Incendie
                </Link>
              </li>
              <li>
                <Link to="/automation" className="text-white/60 hover:text-white transition-colors text-sm">
                  Domotique
                </Link>
              </li>
              <li>
                <Link to="/superior-alarm-g3" className="text-white/60 hover:text-white transition-colors text-sm">
                  Grade 3 - Sites Sensibles
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-medium text-white mb-6 uppercase tracking-wider">
              Zones d'intervention
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/alarme-montpellier" className="text-emerald-400 hover:text-emerald-300 transition-colors text-sm font-medium">
                  ★ Alarme Montpellier - Devis Gratuit
                </Link>
              </li>
              <li>
                <Link to="/alarme-ajax-herault" className="text-white/60 hover:text-white transition-colors text-sm">
                  Alarme Hérault (34)
                </Link>
              </li>
              <li>
                <Link to="/alarme-ajax-nimes" className="text-white/60 hover:text-white transition-colors text-sm">
                  Alarme Nîmes / Gard (30)
                </Link>
              </li>
              <li>
                <Link to="/alarme-ajax-beziers" className="text-white/60 hover:text-white transition-colors text-sm">
                  Alarme Béziers (34)
                </Link>
              </li>
              <li>
                <Link to="/alarme-ajax-narbonne" className="text-white/60 hover:text-white transition-colors text-sm">
                  Alarme Narbonne / Aude (11)
                </Link>
              </li>
              <li>
                <Link to="/alarme-ajax-perpignan" className="text-white/60 hover:text-white transition-colors text-sm">
                  Alarme Perpignan (66)
                </Link>
              </li>
              <li>
                <Link to="/alarme-ajax-marseille" className="text-white/60 hover:text-white transition-colors text-sm">
                  Alarme Marseille (13)
                </Link>
              </li>
              <li>
                <Link to="/installation" className="text-white/60 hover:text-white transition-colors text-sm">
                  Toutes nos zones
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-medium text-white mb-6 uppercase tracking-wider">
              Contact
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-white/50 flex-shrink-0 mt-0.5" />
                <span className="text-white/60 text-sm">
                  {siteConfig.address.street}<br />
                  {siteConfig.address.complement}<br />
                  {siteConfig.address.postalCode} {siteConfig.address.city}
                </span>
              </li>
              <li>
                <a 
                  href={`tel:${siteConfig.contact.phoneFormatted}`} 
                  className="flex items-center gap-3 text-white/60 hover:text-white transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 text-white/50 flex-shrink-0" />
                  {siteConfig.contact.phone}
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${siteConfig.contact.email}`} 
                  className="flex items-center gap-3 text-white/60 hover:text-white transition-colors text-sm"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-white/50 flex-shrink-0" />
                  {siteConfig.contact.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            © {currentYear} FGS Alarme Montpellier. Tous droits réservés.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/contact" className="text-white/50 hover:text-white transition-colors text-sm">
              Contact
            </Link>
            <Link to="/blog" className="text-white/50 hover:text-white transition-colors text-sm">
              Blog
            </Link>
            <Link to="/ai-security" className="text-white/50 hover:text-white transition-colors text-sm">
              Protection IA
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <img 
              src="https://ajax.systems/assets/logo-dark.svg" 
              alt="Ajax Systems Partner" 
              className="h-5 opacity-40"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
